import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Hidden from '@material-ui/core/Hidden';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import classNames from 'classnames';
import { ADGMBreadcrumbs, ADGMSwitch, ADGMSnackbar, Loading } from 'aurora-components';
import { keyEmptyCheck } from 'aurora-utils/utils';
import FILTERS_NAMES from 'aurora-utils/filtersNames';

import { fetchFilterData, clearFilterData, fetchFiltersDropDowns } from 'aurora-composition/modules/filters/actions';
import { useDeepEffect } from '../../hooks';
import {
  getInitialState,
  FILTERS_DATA,
  DEPENDENT_FILTERS,
  FILTERS_WITH_FILTERED_OPTIONS,
  isEmptyObject,
  FIELD_TYPES,
  getDropdownData,
  getFilteredDropdownData,
} from './constants';
import SearchField from './SearchField';
import Sidebar from './sideBar';
import TopBar from './TopBar';
import MobileHeader from './MobileHeader';
import FiltersModal from './FiltersModal';
import TopFilters from './TopFilters';

import styles from './styles';

/** ****************
Props Documentation:
--------------------
breadCrumbs : array of breadCrumbs data
***************** */

const ADGMFilters = ({
  classes,
  children,
  loading,
  title,
  showTitle,
  hideSideBar,
  hideTopbar,
  totalResults,
  breadCrumbs,
  switchOptions,
  selectedSwitch,
  propertyFilterOptions,
  leaseTypeFilterOptions,
  selectedPropertyFilter,
  selectedLeaseTypeFilter,
  query,
  requiredFilters = [],
  showSearchField,
  searchFieldValue,
  searchFieldPlaceHolder,
  error,
  onSwitchChange,
  onPropertyFilterChange,
  onLeaseTypeFilterChange,
  onSearch,
  onFiltersReset,
  customClasses,
  filterModalTitle,
  filtersApiParams,
  showClearAll,
  filterTitle,
  filterBtnTitle,
  autoOpenFilterModal,
  isPMABank,
  REQUIRED_SIDEBAR_FILTERS_LENGTH,
  requiredFiltersDropDowns,
}) => {
  const formDataInitialState = useMemo(() => getInitialState(requiredFilters), [requiredFilters]);
  const [formData, setFormData] = useState(formDataInitialState);
  const [searchValue, setSearchValue] = useState(searchFieldValue);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [openModal, setOpenModal] = useState(autoOpenFilterModal);
  const [parentFilterChangeToggler, setParentFilterChangeToggler] = useState(false);
  const { loading: dropDownsLoading, dropDowns } = useSelector(stateReducer => stateReducer.filtersReducer);

  const dispatch = useDispatch();

  useDeepEffect(() => {
    let updatedFormState = Object.keys(formDataInitialState)?.reduce((state, filterName) => {
      const data = query[filterName] ?? null;
      const updatedState = { ...state };
      const parentFilterName = Object.keys(DEPENDENT_FILTERS)?.find(
        filterKey => DEPENDENT_FILTERS[filterKey] === filterName,
      );
      if (parentFilterName && !query[parentFilterName]) {
        updatedState[filterName] = FILTERS_DATA[filterName]?.multi ? [] : null;
      } else if (FILTERS_DATA[filterName]?.multi === true) {
        if (isArray(data)) {
          updatedState[filterName] = data?.map(item => (Number.isNaN(+item) ? item : +item));
        } else if (data) {
          updatedState[filterName] = [Number.isNaN(+data) ? data : +data];
        }
      } else if (data === 'true' || data === 'false') {
        updatedState[filterName] = data === 'true' ? 1 : query?.isBlocked === 'false' ? 2 : null;
      } else {
        updatedState[filterName] = data && !Number.isNaN(+data) ? +data : data;
      }
      return updatedState;
    }, {});

    if (query?.prefix) {
      updatedFormState = {
        ...updatedFormState,
        prefix: query.prefix,
      };
    }

    const updatedAppliedFilters = Object.keys(updatedFormState)?.reduce((result, filterKey) => {
      const data = updatedFormState[filterKey];
      return {
        ...result,
        ...((typeof data === 'number' || !isEmpty(data)) && {
          [filterKey]: data,
        }),
      };
    }, {});

    setFormData(updatedFormState);
    setAppliedFilters(updatedAppliedFilters);

    // if (requiredFilters?.includes(FILTERS_NAMES?.POA_OWNERS) && query[FILTERS_NAMES?.AUTHORIZATION] === '2') {
    //   dispatch(fetchFilterData(FILTERS_NAMES?.POA_OWNERS));
    // } else
    // if (requiredFilters?.includes(FILTERS_NAMES?.POA_OWNERS)) {
    //   dispatch(clearFilterData(FILTERS_NAMES?.POA_OWNERS));
    // }
  }, [query, formDataInitialState]);

  const filtersWithDropDowns = useMemo(() => {
    return requiredFilters?.filter(
      filter =>
        !!FILTERS_DATA[filter]?.dropDownName && !isArray(requiredFiltersDropDowns[FILTERS_DATA[filter]?.dropDownName]),
    );
  }, [requiredFilters, requiredFiltersDropDowns]);

  useEffect(() => {
    const params = {
      ...(filtersWithDropDowns?.includes(FILTERS_NAMES?.CONTRACT_CLASSIFICATION_ID) && {
        [FILTERS_NAMES?.CONTRACT_CLASSIFICATION_ID]: {
          contractType: 'PropertyManagementAgreement',
        },
      }),
      ...filtersApiParams,
    };
    dispatch(fetchFiltersDropDowns(filtersWithDropDowns, params));
  }, [!isEmpty(switchOptions) ? requiredFilters : null]);

  useEffect(() => {
    setSearchValue(searchFieldValue);
  }, [searchFieldValue]);

  const isLoading = useMemo(() => {
    return loading || dropDownsLoading;
  }, [loading, dropDownsLoading]);

  const filteredLocalDropDowns = useMemo(() => {
    return filtersWithDropDowns?.reduce((result, filter) => {
      const parentFilterName = Object.keys(FILTERS_WITH_FILTERED_OPTIONS)?.find(
        filterKey =>
          isObject(FILTERS_WITH_FILTERED_OPTIONS[filterKey]) &&
          FILTERS_WITH_FILTERED_OPTIONS[filterKey]?.childFilter === filter,
      );
      if (!isArray(dropDowns[filter]) && parentFilterName) {
        const filterData = FILTERS_WITH_FILTERED_OPTIONS[parentFilterName];
        return {
          ...result,
          [FILTERS_DATA[filter]?.dropDownName]: getFilteredDropdownData(
            FILTERS_DATA[filter]?.dropDownName,
            formData[parentFilterName],
            filterData?.filteringKey,
          ),
        };
      }
      return result;
    }, {});
  }, [filtersWithDropDowns, dropDowns, parentFilterChangeToggler]);

  const localDropDowns = useMemo(() => {
    return filtersWithDropDowns?.reduce((result, filter) => {
      const parentFilterName = Object.keys(FILTERS_WITH_FILTERED_OPTIONS)?.find(
        filterKey =>
          isObject(FILTERS_WITH_FILTERED_OPTIONS[filterKey]) &&
          FILTERS_WITH_FILTERED_OPTIONS[filterKey]?.childFilter === filter,
      );
      if (!isArray(dropDowns[filter]) && !parentFilterName) {
        return {
          ...result,
          [FILTERS_DATA[filter]?.dropDownName]: getDropdownData(FILTERS_DATA[filter]?.dropDownName),
        };
      }
      return result;
    }, {});
  }, [filtersWithDropDowns, dropDowns]);

  const dropDownsData = useMemo(() => {
    return {
      ...localDropDowns,
      ...filteredLocalDropDowns,
      ...dropDowns,
      ...requiredFiltersDropDowns,
    };
  }, [dropDowns, filteredLocalDropDowns, localDropDowns, requiredFiltersDropDowns]);

  const handleChange = (name, value) => {
    const dependentFiltersState = {};

    if (requiredFilters?.includes(FILTERS_NAMES?.POA_OWNERS) && name === FILTERS_NAMES?.AUTHORIZATION) {
      if (value === 2) {
        dispatch(fetchFilterData(FILTERS_NAMES?.POA_OWNERS));
      } else {
        dispatch(clearFilterData(FILTERS_NAMES?.POA_OWNERS));
        dependentFiltersState[FILTERS_NAMES?.POA_OWNERS] = [];
      }
    }

    if (typeof value !== 'number' && !value) {
      let dependentFilterName = DEPENDENT_FILTERS[name];

      while (dependentFilterName) {
        dependentFiltersState[dependentFilterName] = FILTERS_DATA[dependentFilterName]?.multi ? [] : null;
        dependentFilterName = DEPENDENT_FILTERS[dependentFilterName];
      }
    } else if (
      FILTERS_DATA[name]?.type === FIELD_TYPES?.DATE &&
      DEPENDENT_FILTERS[name] &&
      FILTERS_DATA[DEPENDENT_FILTERS[name]]?.type === FIELD_TYPES?.DATE &&
      moment(formData[DEPENDENT_FILTERS[name]]).isValid()
    ) {
      dependentFiltersState[DEPENDENT_FILTERS[name]] =
        moment(value).isValid() && moment(value)?.isAfter(formData[DEPENDENT_FILTERS[name]])
          ? null
          : formData[DEPENDENT_FILTERS[name]];
    }

    if (isObject(FILTERS_WITH_FILTERED_OPTIONS[name])) {
      setParentFilterChangeToggler(curVal => !curVal);
    }

    setFormData(data => ({
      ...data,
      ...dependentFiltersState,
      [name]: value,
    }));
  };

  const handleSearchFieldChange = searchedValue => {
    setSearchValue(searchedValue);
  };

  const handleSwitch = value => {
    onSwitchChange(value);
    dispatch(clearFilterData(FILTERS_NAMES?.POA_OWNERS));
    setFormData(getInitialState(requiredFilters));
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSearch = () => {
    const searchedFiltersData = Object.keys(formData)?.reduce((curFiltersData, formKey) => {
      let data = null;
      if (
        (isArray(formData[formKey]) && !isEmpty(formData[formKey])) ||
        (!isArray(formData[formKey]) &&
          formData[formKey] !== undefined &&
          formData[formKey] !== null &&
          formData[formKey] !== '')
      ) {
        data =
          FILTERS_DATA[formKey]?.type === FIELD_TYPES?.FIELD && isString(formData[formKey])
            ? formData[formKey]?.trim()
            : formData[formKey];
      }
      return {
        ...curFiltersData,
        ...((data === 0 || !!data) && { [formKey]: data }),
      };
    }, {});
    onSearch(searchedFiltersData, isString(searchValue) ? searchValue.trim() : searchValue);
  };

  const handleRemoveFilter = useCallback(
    (updatedFilters, updatedSearchValue) => {
      onSearch(updatedFilters, isString(updatedSearchValue) ? updatedSearchValue.trim() : updatedSearchValue);
    },
    [query],
  );

  const handleResetFilters = () => {
    if (requiredFilters?.includes(FILTERS_NAMES?.AUTHORIZATION)) {
      dispatch(clearFilterData(FILTERS_NAMES?.POA_OWNERS));
    }

    if (!isEmptyObject(appliedFilters)) {
      onFiltersReset();
    }

    setFormData(getInitialState(requiredFilters));
    setSearchValue(null);
  };

  return (
    <div className={classNames(classes.root, customClasses?.root)}>
      {isLoading && <Loading />}
      {error && <ADGMSnackbar message={keyEmptyCheck(error?.message || error)} />}
      {isArray(breadCrumbs) && !isEmpty(breadCrumbs) && (
        <Hidden xsDown>
          <ADGMBreadcrumbs paths={breadCrumbs} />
        </Hidden>
      )}
      <Hidden smUp>
        <MobileHeader heading={title} appliedFilters={appliedFilters} onClickHandler={handleOpenModal} />
      </Hidden>
      {showTitle && title && !hideSideBar && (
        <Hidden xsDown>
          <div className={classes.titleContainer}>
            <Typography variant='h5' className={classes.title}>
              {keyEmptyCheck(title)}
            </Typography>
          </div>
        </Hidden>
      )}
      {isArray(switchOptions) && !isEmpty(switchOptions) && (
        <ADGMSwitch
          options={switchOptions}
          selected={selectedSwitch}
          handleChange={handleSwitch}
          customClasses={{ container: classes.switchContainer }}
        />
      )}
      <div className={classes.content}>
        {!hideSideBar && (
          <div className={classes.sideBarContent}>
            {showSearchField && (
              <div className={classes.searchFieldContainer}>
                <SearchField
                  value={searchValue}
                  placeHolder={searchFieldPlaceHolder}
                  onChange={handleSearchFieldChange}
                  onSearchHandler={handleSearch}
                />
              </div>
            )}
            <Hidden smDown>
              <div className={classes.sideBar}>
                <Sidebar
                  formData={formData}
                  filters={requiredFilters}
                  appliedFilters={appliedFilters}
                  propertyFilterOptions={propertyFilterOptions}
                  selectedPropertyFilter={selectedPropertyFilter}
                  leaseTypeFilterOptions={leaseTypeFilterOptions}
                  selectedLeaseTypeFilter={selectedLeaseTypeFilter}
                  dropDowns={dropDownsData}
                  handleChange={handleChange}
                  handlePrefixChange={val => handleChange('prefix', val)}
                  propertyFiltersHandler={onPropertyFilterChange}
                  leaseTypeFilterHandler={onLeaseTypeFilterChange}
                  showFiltersModalHandler={handleOpenModal}
                  searchHandler={handleSearch}
                  filtersResetHandler={handleResetFilters}
                  showClearAll={showClearAll}
                  filterTitle={filterTitle}
                  filterBtnTitle={filterBtnTitle}
                  isPMABank={isPMABank}
                  REQUIRED_SIDEBAR_FILTERS_LENGTH={REQUIRED_SIDEBAR_FILTERS_LENGTH}
                />
              </div>
            </Hidden>
          </div>
        )}
        {!loading && (
          <div className={classes.dataContainer}>
            {hideSideBar && (
              <TopFilters
                title={showTitle ? title : ''}
                requiredFilters={requiredFilters}
                showSearchField={showSearchField}
                searchFieldPlaceHolder={searchFieldPlaceHolder}
                searchFieldValue={searchValue}
                isActiveFilters={!isEmpty(appliedFilters)}
                searchFieldChangeHandler={handleSearchFieldChange}
                searchHandler={handleSearch}
                showFiltersModalHandler={handleOpenModal}
              />
            )}
            {!hideTopbar && (
              <TopBar
                totalResults={totalResults}
                appliedFilters={appliedFilters}
                searchValue={searchFieldValue}
                dropDowns={dropDownsData}
                removeFilterHandler={handleRemoveFilter}
              />
            )}
            <div className={classes.dataList}>{children}</div>
          </div>
        )}
        {openModal === true && (
          <FiltersModal
            loading={isLoading}
            openModal={openModal}
            formData={formData}
            filters={requiredFilters}
            appliedFilters={appliedFilters}
            propertyFilterOptions={propertyFilterOptions}
            selectedPropertyFilter={selectedPropertyFilter}
            selectedLeaseTypeFilter={selectedLeaseTypeFilter}
            dropDowns={dropDownsData}
            handleChange={handleChange}
            propertyFiltersHandler={onPropertyFilterChange}
            searchHandler={handleSearch}
            closeModalHandler={handleCloseModal}
            filtersResetHandler={handleResetFilters}
            customClasses={customClasses}
            filterModalTitle={filterModalTitle}
            showClearAll={showClearAll}
            filterBtnTitle={filterBtnTitle}
            handlePrefixChange={val => handleChange('prefix', val)}
            isPMABank={isPMABank}
          />
        )}
      </div>
    </div>
  );
};

ADGMFilters.propTypes = {
  classes: PropTypes.array.isRequired,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  showTitle: PropTypes.bool,
  hideSideBar: PropTypes.bool,
  hideTopbar: PropTypes.bool,
  totalResults: PropTypes.number,
  breadCrumbs: PropTypes.array,
  switchOptions: PropTypes.array,
  selectedSwitch: PropTypes.any,
  propertyFilterOptions: PropTypes.array,
  leaseTypeFilterOptions: PropTypes.array,
  selectedPropertyFilter: PropTypes.any,
  selectedLeaseTypeFilter: PropTypes.any,
  query: PropTypes.object.isRequired,
  requiredFilters: PropTypes.object.isRequired,
  showSearchField: PropTypes.bool,
  searchFieldValue: PropTypes.any,
  searchFieldPlaceHolder: PropTypes.string,
  error: PropTypes.object,
  onSwitchChange: PropTypes.func,
  onPropertyFilterChange: PropTypes.func,
  onLeaseTypeFilterChange: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
  onFiltersReset: PropTypes.func.isRequired,
  customClasses: PropTypes.object.isRequired,
  filterModalTitle: PropTypes.string,
  filtersApiParams: PropTypes.object,
  showClearAll: PropTypes.string.isRequired,
  filterTitle: PropTypes.string.isRequired,
  filterBtnTitle: PropTypes.string.isRequired,
  autoOpenFilterModal: PropTypes.object.isRequired,
  isPMABank: PropTypes.bool,
  REQUIRED_SIDEBAR_FILTERS_LENGTH: PropTypes.number,
  requiredFiltersDropDowns: PropTypes.object,
};

ADGMFilters.defaultProps = {
  loading: false,
  showTitle: false,
  hideSideBar: false,
  hideTopbar: false,
  totalResults: null,
  breadCrumbs: [],
  switchOptions: [],
  selectedSwitch: null,
  propertyFilterOptions: [],
  leaseTypeFilterOptions: [],
  selectedPropertyFilter: null,
  selectedLeaseTypeFilter: null,
  showSearchField: false,
  searchFieldValue: null,
  searchFieldPlaceHolder: '',
  error: null,
  filterModalTitle: 'show_more_filter',
  filtersApiParams: {},
  onSwitchChange: () => {},
  onPropertyFilterChange: () => {},
  onLeaseTypeFilterChange: () => {},
  isPMABank: false,
  REQUIRED_SIDEBAR_FILTERS_LENGTH: 5,
  requiredFiltersDropDowns: {},
};
export default withStyles(styles)(ADGMFilters);
